import { IDetailsGroupDividerProps, IGroup, IGroupHeaderProps, IRenderFunction } from '@fluentui/react';
import { baseTableListRowHoverClasses } from 'components/TableList/BaseTableList';

export interface ISelectableGroupHeaderOptions {
  isGroupSelected: (group: IGroup) => boolean;
  onGroupSelect: (group: IGroup) => void;
}

// This styles object replaces anything set via groupProps.headerProps.styles: consumers that need
// extra header styling must extend this helper instead of passing their own headerProps.styles.
const selectableGroupHeaderStyles: IGroupHeaderProps['styles'] = {
  root: [
    baseTableListRowHoverClasses.groupHeader,
    {
      selectors: {
        '&.is-selected .showOnRowHover': { opacity: 1 },
      },
    },
  ],
  // Stretch the native title element (the only per-group click target GroupHeader exposes) over the
  // remaining row surface so onGroupHeaderClick fires from anywhere on the header row.
  title: {
    flexGrow: 1,
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
  },
};

//
// Makes group headers select like rows: clicking anywhere on the header row (except the collapse
// chevron and any content that stops propagation) calls onGroupSelect, and the selected group gets
// Fluent's native selected-header background. Toggle/deselect semantics belong to the consumer's
// onGroupSelect. Pass the result as groupProps.onRenderHeader; custom title content must be supplied
// via groupProps.headerProps.onRenderName so it stays inside the clickable title element.
//
export const buildSelectableGroupHeaderRenderer = (
  options: ISelectableGroupHeaderOptions,
): IRenderFunction<IDetailsGroupDividerProps> => {
  const onRenderHeader: IRenderFunction<IDetailsGroupDividerProps> = (props, defaultRender) => {
    if (!props?.group || !defaultRender) {
      return defaultRender?.(props) ?? null;
    }

    const headerProps: IDetailsGroupDividerProps & Pick<IGroupHeaderProps, 'styles'> = {
      ...props,
      selected: options.isGroupSelected(props.group),
      onGroupHeaderClick: options.onGroupSelect,
      styles: selectableGroupHeaderStyles,
    };

    // Without this wrapper, SelectionZone treats clicks on the header surface as a range selection
    // and silently selects the last row of the group. Rows are siblings of the header, not children,
    // so disabling selection here only affects the header row itself.
    return <div data-selection-disabled={true}>{defaultRender(headerProps)}</div>;
  };

  return onRenderHeader;
};
